import { supabase } from './supabaseClient';
import { DEFAULT_QUOTE_SECTIONS } from '../types';
import type { Quote, QuoteDetails, QuoteOrderLine, QuoteOrderTranche, QuoteSupplementRef, ServiceItem } from '../types';
import type { FlatInclusionDecision } from './flatRateLogic';
import { pendingFlatInclusionDecisions } from './flatRateLogic';
import type { SupplementMapping } from './quoteSupplementLogic';
import { isSupplementQuote, nextSupplementSeq, pickMainQuote, planSupplementForProjectLv } from './quoteSupplementLogic';
import { loadProjectById } from './cloudService';
import {
    attachAcceptState,
    createQuote,
    mapQuoteRow,
    QuoteConflictError,
    QuoteNotSavedError,
    toQuoteRow,
    type QuoteAcceptFailureState,
} from './crmService';
import { nextDocumentNumber } from './documentNumberService';
import { calculateQuoteTotals, canTransition, computePosNumbers, typeDigitFromDocumentNumber } from './quoteLogic';
import { unionFollowUpLog } from './quoteMerge';
import { typzifferFuerProjektTyp } from './quoteAcceptDefaults';
import {
    buildTranche,
    normalizeOrderLines,
    openOrderLines,
    quoteOrderSummary,
    statusAfterLines,
    validateOrderLines,
} from './quoteOrderTranches';
import { applyTrancheToProject, assertProjectWritable, type LoadedProject } from './quoteOrderService';
import { fetchOrderValue } from './erpSteeringService';
import { isMissingOrderTranchesSchema } from './quoteOrderSchema';
import { describeError } from './errorMessage';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * NACHTRAGSANGEBOT — Schreibwege
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Rechenkern: quoteSupplementLogic.ts. Hier: Zielprojekt suchen und laden,
 * leeres Nachtragsangebot anlegen, Angebot als Nachtrag annehmen.
 *
 * Die Annahme folgt acceptQuote (crmService) — Claim verankert auf
 * updated_at, Kompensation, vollständiger angenommener Stand, Fallback-Link —
 * mit einem Unterschied, der alles Weitere bestimmt: es entsteht KEIN Projekt.
 * Das Projekt-LV wird über applyTrancheToProject (quoteOrderService)
 * angepasst, also über denselben wiederholbaren Weg wie „Erneut eintragen":
 * die Projektnotiz trägt die id des Auftragsteils, ein zweiter Lauf erhöht
 * nichts doppelt.
 *
 * Zurückgenommen wird nur, solange das Projekt-Speichern nicht versucht
 * wurde, und nur auf den eigenen letzten Stand (verankert). Danach bleibt die
 * Annahme stehen; scheitert das Eintragen, kommt `applied: false` zurück und
 * „Erneut eintragen" holt es nach.
 *
 * VERTRAG — Signaturen sind fest (Pakete B/C rufen sie auf).
 */

/** Höchstzahl der Treffer in der Projektauswahl. */
const SEARCH_LIMIT = 50;

/** Ein Projekt in der Auswahl „Nachtrag zu …". */
export interface SupplementProjectOption {
    id: string;
    projectNumber: string;
    projectName: string;
    customerId?: string | null;
    /** Warum kein Nachtrag eingetragen werden kann (Archiv, Tochter) — Auswahl gesperrt. */
    blocker?: string;
}

/** Zeile der Projektsuche — nur Spalten, kein ganzes `data`. */
interface ProjectSearchRow {
    id: string;
    name: string | null;
    project_number: string | null;
    customer_id: string | null;
    status: string | null;
    parent_project_id: string | null;
    updated_at: string | null;
    gd_status: string | null;
}

// Status steht doppelt (Spalte und JSON, projectBilling.ProjectStatusSource) —
// beide werden gelesen. Die Tochter erkennt die Spalte parent_project_id
// (Nr. 71, die Wahrheit; das Blob-Feld setzt der Lader daraus zusammen).
const PROJECT_SEARCH_COLUMNS =
    'id, name, project_number, customer_id, status, parent_project_id, updated_at, gd_status:data->metadata->globalData->>status';

/**
 * Suchtext für den PostgREST-Filter: Trennzeichen der `or`-Syntax und
 * Platzhalter raus, Leerraum wird zum Platzhalter („neubau halle" findet
 * „Neubau Lagerhalle").
 */
const searchPattern = (query: string): string => {
    const cleaned = String(query ?? '').replace(/[%_*,()"'\\]/g, ' ').trim().replace(/\s+/g, '%');
    return cleaned ? `%${cleaned}%` : '';
};

const toProjectOption = (r: ProjectSearchRow): SupplementProjectOption => {
    let blocker: string | undefined;
    try {
        assertProjectWritable({ status: r.status, parent_project_id: r.parent_project_id, data: { metadata: { globalData: { status: r.gd_status } } } }, 'nachtrag');
    } catch (e) {
        blocker = (e as Error).message;
    }
    return {
        id: r.id,
        projectNumber: r.project_number || '',
        projectName: r.name || '',
        customerId: r.customer_id ?? null,
        ...(blocker ? { blocker } : {}),
    };
};

/**
 * Projekte für die Auswahl. Mit `customerId`: dessen Projekte zuerst, dann
 * Treffer der Suche über Projektnummer/-name. Begrenzte Trefferzahl (≤ 50),
 * keine ungepagte Gesamtliste.
 *
 * Ohne Suchtext: die Projekte des Kunden bzw. die zuletzt geänderten.
 * Mandantentrennung wie bei den übrigen Projekt-Ladern über die RLS von
 * `projects` (loadProjectsFromCloud filtert ebenfalls nicht selbst).
 * Archivierte und Töchter erscheinen mit `blocker` — sichtbar, aber gesperrt.
 */
export const searchSupplementProjects = async (
    query: string,
    customerId?: string,
): Promise<SupplementProjectOption[]> => {
    const pattern = searchPattern(query);
    const request = (forCustomer?: string) => {
        let q = supabase.from('projects').select(PROJECT_SEARCH_COLUMNS);
        if (forCustomer) q = q.eq('customer_id', forCustomer);
        if (pattern) q = q.or(`project_number.ilike.${pattern},name.ilike.${pattern}`);
        return q.order('updated_at', { ascending: false }).limit(SEARCH_LIMIT);
    };
    // Mit Kunde: seine Projekte (ggf. gefiltert) UND — nur mit Suchtext — die
    // übrigen Treffer. Ohne Kunde: Treffer bzw. zuletzt geänderte.
    const results = await Promise.all([
        ...(customerId ? [request(customerId)] : []),
        ...(pattern || !customerId ? [request()] : []),
    ]);
    const rows: ProjectSearchRow[] = [];
    for (const { data, error } of results) {
        if (error) throw new Error(describeError(error));
        rows.push(...((data || []) as unknown as ProjectSearchRow[]));
    }

    const seen = new Set<string>();
    const unique = rows.filter((r) => {
        if (!r?.id || seen.has(r.id)) return false;
        seen.add(r.id);
        return true;
    });
    const own = (r: ProjectSearchRow) => (customerId && r.customer_id === customerId ? 0 : 1);
    return unique
        .sort((a, b) => own(a) - own(b) || String(b.updated_at || '').localeCompare(String(a.updated_at || '')))
        .slice(0, SEARCH_LIMIT)
        .map(toProjectOption);
};

/** Was Dialog und Editor vom Zielprojekt brauchen — einmal geladen, ohne zu schreiben. */
export interface SupplementContext {
    projectId: string;
    projectNumber: string;
    projectName: string;
    customerId?: string | null;
    services: ServiceItem[];
    endDate?: string;
    /** Archiv/Tochter — sperrt die Annahme als Nachtrag. */
    blocker?: string;
    /** Bisher fixierter Auftragswert (Cent), null = keiner / nicht lesbar. */
    orderValueCents: number | null;
    orderValueNote?: string;
    /** Nachtragsnummer für dieses Angebot (vorhandene seq behalten, sonst nächste freie). */
    seq: number;
    /** Hauptangebot des Projekts, falls vorhanden (pickMainQuote). */
    mainQuote?: { id: string; quoteNumber: string; orderNumber?: string; acceptedAt?: string };
}

/**
 * Nachtragsnummer für `forQuote` am Projekt: seine vorhandene Nummer, wenn
 * sie zu diesem Projekt gehört und kein ANDERES Angebot sie trägt — sonst
 * die nächste freie. Gezählt werden alle Angebote mit
 * details.nachtragZu.projectId = projectId (auch abgelehnte: eine Nummer wird
 * nicht zweimal vergeben, wie bei Belegnummern).
 */
const supplementSeqFor = async (projectId: string, forQuote?: Pick<Quote, 'id' | 'details'>): Promise<number> => {
    const { data, error } = await supabase
        .from('quotes')
        .select('id, details')
        .eq('details->nachtragZu->>projectId', projectId);
    if (error) throw new Error(describeError(error));
    const others = ((data || []) as { id: string; details: QuoteDetails | null }[])
        .filter((r) => r.id !== forQuote?.id)
        .map((r) => Number(r.details?.nachtragZu?.seq))
        .filter((n) => Number.isInteger(n) && n > 0);
    const own = forQuote?.details?.nachtragZu;
    const ownSeq = Number(own?.seq);
    if (own?.projectId === projectId && Number.isInteger(ownSeq) && ownSeq > 0 && !others.includes(ownSeq)) return ownSeq;
    return nextSupplementSeq(others);
};

/**
 * Hauptangebot des Projekts (vollständig — createSupplementQuote übernimmt
 * Texte und Steuer daraus). Best effort: ohne Hauptangebot geht es weiter.
 * `select('*')`, damit eine Datenbank ohne `order_tranches` (Nr. 140) nicht
 * mit 42703 abbricht.
 */
const loadMainQuote = async (projectId: string, excludeId?: string): Promise<Quote | undefined> => {
    try {
        const { data, error } = await supabase
            .from('quotes')
            .select('*, customers(name)')
            .eq('project_id', projectId)
            .limit(SEARCH_LIMIT);
        if (error) throw error;
        const quotes = ((data || []) as Record<string, unknown>[]).map(mapQuoteRow).filter((q) => q.id !== excludeId);
        return pickMainQuote(quotes, projectId);
    } catch (e) {
        console.warn('[quoteSupplementService] Hauptangebot nicht ladbar — weiter ohne:', e);
        return undefined;
    }
};

interface LoadedSupplementContext {
    ctx: SupplementContext;
    project: LoadedProject;
    mainQuote?: Quote;
}

const loadContext = async (projectId: string, forQuote?: Pick<Quote, 'id' | 'details'>): Promise<LoadedSupplementContext> => {
    if (!projectId) throw new Error('Kein Projekt gewählt.');
    // Nur lesen: KEIN Speicher-Anker (Muster 6185) — ist das Projekt im
    // Editor offen, bliebe dessen Ausgangsstand sonst nicht der seine.
    // Geschrieben wird später über applyTrancheToProject, das frisch lädt.
    const project = (await loadProjectById(projectId, undefined, { skipMigration: true, ohneAnker: true })) as LoadedProject | null;
    if (!project) throw new Error('Das Projekt wurde nicht gefunden (gelöscht?).');
    let blocker: string | undefined;
    try { assertProjectWritable(project, 'nachtrag'); } catch (e) { blocker = (e as Error).message; }
    const [ov, seq, main] = await Promise.all([
        fetchOrderValue(projectId),
        supplementSeqFor(projectId, forQuote),
        loadMainQuote(projectId, forQuote?.id),
    ]);
    const meta = project.data?.metadata;
    // Datum des Hauptauftrags: das Beauftragungsdatum des ersten Teils (so,
    // wie der Kunde bestellt hat), sonst der Annahmezeitpunkt.
    const mainOrderDate = main ? (quoteOrderSummary(main).firstOrderDate || main.acceptedAt || '') : '';
    const ctx: SupplementContext = {
        projectId,
        projectNumber: meta?.projectNumber || project.project_number || '',
        projectName: meta?.projectName || project.name || '',
        customerId: project.customer_id ?? null,
        services: Array.isArray(meta?.globalData?.services) ? meta.globalData.services : [],
        ...(meta?.globalData?.endDate ? { endDate: meta.globalData.endDate } : {}),
        ...(blocker ? { blocker } : {}),
        orderValueCents: ov ? ov.orderValueCents : null,
        ...(ov?.note ? { orderValueNote: ov.note } : {}),
        seq,
        ...(main
            ? {
                  mainQuote: {
                      id: main.id,
                      quoteNumber: main.quoteNumber,
                      ...(main.orderNumber ? { orderNumber: main.orderNumber } : {}),
                      ...(mainOrderDate ? { acceptedAt: mainOrderDate } : {}),
                  },
              }
            : {}),
    };
    return { ctx, project, mainQuote: main };
};

/**
 * Zielprojekt laden. `forQuote`: das Nachtragsangebot, um dessen seq zu
 * behalten bzw. es selbst beim Zählen auszulassen. Wirft, wenn das Projekt fehlt.
 * `mainQuote.acceptedAt` ist das Datum des Hauptauftrags (Beauftragungsdatum
 * des ersten Teils, sonst Annahmezeitpunkt).
 */
export const loadSupplementContext = async (
    projectId: string,
    forQuote?: Pick<Quote, 'id' | 'details'>,
): Promise<SupplementContext> => (await loadContext(projectId, forQuote)).ctx;

/**
 * Bezug des Angebots auf das Projekt (details.nachtragZu) — eingefroren bei
 * Anlage und Annahme. Was der Kontext nicht (mehr) kennt, bleibt vom
 * bisherigen Bezug desselben Projekts stehen.
 */
const supplementRef = (
    ctx: SupplementContext,
    previous: QuoteSupplementRef | undefined,
    main?: { id: string; quoteNumber: string; orderNumber?: string; orderDate?: string },
): QuoteSupplementRef => {
    const keep = previous && previous.projectId === ctx.projectId ? previous : undefined;
    const m = main ?? (ctx.mainQuote
        ? { id: ctx.mainQuote.id, quoteNumber: ctx.mainQuote.quoteNumber, orderNumber: ctx.mainQuote.orderNumber, orderDate: ctx.mainQuote.acceptedAt }
        : undefined);
    const ref: QuoteSupplementRef = { ...(keep || {}), projectId: ctx.projectId, seq: ctx.seq };
    if (ctx.projectNumber) ref.projectNumber = ctx.projectNumber;
    if (ctx.projectName) ref.projectName = ctx.projectName;
    if (m) {
        ref.mainQuoteId = m.id;
        ref.mainQuoteNumber = m.quoteNumber;
        if (m.orderNumber) ref.mainOrderNumber = m.orderNumber;
        if (m.orderDate) ref.mainOrderDate = String(m.orderDate).slice(0, 10);
    }
    return ref;
};

/** Eingaben des Annahmedialogs im Modus „Nachtrag zu bestehendem Projekt". */
export interface AcceptSupplementValues {
    projectId: string;
    mapping: SupplementMapping;
    /** Teilbeauftragung; fehlt = vollständig (alle offenen Mengen). */
    orderLines?: QuoteOrderLine[];
    orderDate?: string;
    customerRef?: string;
    /** Monatspauschale ja/nein je NEU angehängter Position (ids aus SupplementPlan.newServiceIds). */
    flatDecisions?: Partial<Record<string, FlatInclusionDecision>>;
    /** Neues Projektende (yyyy-mm-dd); leer = unverändert. */
    endDate?: string;
    /** Auftragswert NEU gesamt in Cent (bisher + Nachtrag); 0/leer = nicht ändern. */
    orderValueCents?: number;
    /**
     * Der Auftragswert, den der Dialog beim Öffnen sah (SupplementContext.
     * orderValueCents; null = keiner). Hat ihn bis zum Schreiben ein anderer
     * Nachtrag geändert, kommt nur der Zuwachs dazu (orderValueCentsToWrite).
     */
    orderValueBaseCents?: number | null;
    actor: { id?: string; name: string };
}

/**
 * Klartext, wenn quotes.order_tranches fehlt. Nicht die Meldung der
 * Teilbeauftragung (die empfiehlt „vollständig annehmen") — ein Nachtrag
 * braucht die Spalte immer, die Zuordnung hängt am Auftragsteil.
 */
export const SUPPLEMENT_NEEDS_ORDER_TRANCHES_MESSAGE =
    'Nachtragsangebote sind in dieser Datenbank noch nicht eingerichtet (Migration Nr. 140 fehlt, Spalte quotes.order_tranches). '
    + 'Bitte die Migration ausführen lassen — bis dahin lässt sich das Angebot nicht als Nachtrag annehmen, auch nicht vollständig.';

export interface AcceptSupplementResult {
    /** Angebot nach dem letzten erfolgreichen Schreiben — neuer Anker des Editors. */
    quote: Quote;
    orderNumber: string;
    projectId: string;
    /** Steht der Nachtrag im Projekt-LV? false → „Erneut eintragen" im Beauftragungsstand. */
    applied: boolean;
    applyError?: string;
}

/** Klartext je Angebotsposition: „1.2 Begehung SiGeKo". */
const itemLabel = (quote: Quote, itemId: string): string => {
    const it = (quote.items || []).find((i) => i.id === itemId);
    return it ? `${it.lvPosition || it.posNumber || ''} ${it.title || ''}`.trim() || itemId : itemId;
};

/**
 * Angebot als Nachtrag annehmen — KEIN neues Projekt.
 * Status-Claim verankert auf updated_at (wie acceptQuote) inkl. Auftragsteil
 * (mit serviceIdByItemId), project_id und details.nachtragZu; AB-Nummer;
 * Projekt-LV über saveProjectToCloud (Notiz-id = Teil-id, wiederholbar);
 * appliedAt; Auftragswert best effort.
 * Wirft mit Fehlerzustand (acceptFailureState) wie acceptQuote; scheitert
 * erst das Eintragen ins Projekt, kommt `applied: false` zurück.
 *
 * Keine Kickoff-Mail, keine neue Projektnummer, keine Benachrichtigung — das
 * Projekt läuft bereits.
 */
export const acceptQuoteAsSupplement = async (
    quote: Quote,
    values: AcceptSupplementValues,
): Promise<AcceptSupplementResult> => {
    // a) Statusübergang und Umfang — alles VOR dem ersten Schreiben.
    if (!canTransition(quote.status, 'accepted')) {
        throw new Error(`Angebot kann im Status "${quote.status}" nicht angenommen werden.`);
    }
    if (!values?.projectId) throw new Error('Bitte das Projekt wählen, zu dem der Nachtrag gehört.');
    const fullLines = openOrderLines(quote);
    if (fullLines.length === 0) {
        throw new Error('Das Angebot hat keine beauftragbare Position — ein Nachtrag braucht mindestens eine Menge.');
    }
    const lines = values.orderLines ? normalizeOrderLines(values.orderLines) : fullLines;
    const lineErrors = validateOrderLines(quote, lines);
    if (lineErrors.length) throw new Error(`Umfang der Beauftragung: ${lineErrors.join(' ')}`);

    // b) Zielprojekt FRISCH laden und den Plan rechnen. Was hier sperrt
    //    (Archiv, Tochter, fehlende Zielposition, offene Pauschal-Frage),
    //    sperrt, bevor irgendetwas geschrieben ist.
    const { ctx } = await loadContext(values.projectId, quote);
    if (ctx.blocker) throw new Error(ctx.blocker);
    const plan = planSupplementForProjectLv(ctx.services, quote.items || [], lines, values.mapping || {}, {
        seq: ctx.seq,
        includeNep: true,
    });
    if (plan.missing.length) {
        throw new Error('Einige Zeilen gehören zu keiner Angebotsposition — bitte das Angebot neu laden.');
    }
    if (plan.missingTargets.length) {
        throw new Error(`Zugeordnete Projektposition gibt es nicht mehr: ${plan.missingTargets.map((m) => itemLabel(quote, m.itemId)).join(', ')} — bitte neu zuordnen.`);
    }
    if (pendingFlatInclusionDecisions(plan.services, plan.newServiceIds, values.flatDecisions).length) {
        throw new Error('Für jede neue Position muss entschieden werden: in der Monatspauschale enthalten — ja oder nein.');
    }

    const nowIso = new Date().toISOString();
    const built = buildTranche(quote, {
        id: crypto.randomUUID(),
        at: nowIso,
        kind: 'order',
        lines,
        orderDate: values.orderDate,
        by: values.actor?.id,
        byName: values.actor?.name || '',
        customerRef: values.customerRef,
    });
    // Die Zuordnung reist am Teil — spätere Teile und „Erneut eintragen"
    // lesen sie dort (priorSupplementServiceIds) und sie ist verbindlich.
    // NUR die Zeilen dieses Teils: Positionen ohne Menge zeigt der Dialog
    // nicht, ihre „Wahl" wäre der ungesehene Vorschlag beim Öffnen — als
    // verbindliche Zuordnung sperrte sie „Rest beauftragen" dauerhaft, sobald
    // die vorgeschlagene Projektposition wegfällt (der Teildialog kann nicht
    // neu zuordnen). Für sie gilt beim späteren Teil der dann frische Vorschlag.
    const serviceIdByItemId = plan.serviceIdByItemId;
    const tranche: QuoteOrderTranche = Object.keys(serviceIdByItemId).length > 0
        ? { ...built, serviceIdByItemId }
        : built;
    const targetStatus = statusAfterLines(quote, lines);
    const ref = supplementRef(ctx, quote.details?.nachtragZu);

    // c) Atomarer Claim, an den Stand gebunden, den der Annehmende SIEHT
    //    (wie acceptQuote). project_id und nachtragZu gehen MIT: scheitert
    //    danach auch die Kompensation, steht das Angebot wenigstens als
    //    Nachtrag an seinem Projekt, und „Erneut eintragen" findet beides.
    //    details nur verankert — ohne Anker könnte ein fremder Nachfass-
    //    eintrag verloren gehen (dann schreibt c5 die details).
    const anchor = typeof quote.updatedAt === 'string' && quote.updatedAt.trim() ? quote.updatedAt : null;
    let claim = supabase
        .from('quotes')
        .update({
            status: targetStatus,
            accepted_at: nowIso,
            order_tranches: [tranche],
            project_id: ctx.projectId,
            ...(anchor ? { details: { ...(quote.details || {}), nachtragZu: ref } } : {}),
        })
        .eq('id', quote.id)
        .in('status', ['draft', 'sent']);
    if (anchor) claim = claim.eq('updated_at', anchor);
    const { data: claimed, error: claimErr } = await claim.select('id, details, updated_at');
    if (claimErr) {
        // Ohne Spalte order_tranches (Nr. 140) gibt es keinen Nachtrag: die
        // Zuordnung hängt am Auftragsteil. Nichts ist geschrieben.
        if (isMissingOrderTranchesSchema(claimErr)) throw new Error(SUPPLEMENT_NEEDS_ORDER_TRANCHES_MESSAGE);
        throw claimErr;
    }
    if (!claimed?.length) {
        if (anchor) {
            const { data: current, error: readErr } = await supabase
                .from('quotes').select('*, customers(name)').eq('id', quote.id).maybeSingle();
            if (readErr) throw readErr;
            if (!current) throw new QuoteNotSavedError();
            if (current.status === 'draft' || current.status === 'sent') {
                throw new QuoteConflictError(mapQuoteRow(current));
            }
        }
        throw new Error('Angebot wurde bereits verarbeitet.');
    }
    const claimedUpdatedAt = String(claimed[0].updated_at);
    let lastUpdatedAt = claimedUpdatedAt;
    let lastRow: Quote | null = null;
    let lastDetails = (claimed[0].details ?? undefined) as QuoteDetails | undefined;

    // d) Kompensation: Claim KOMPLETT zurück (Status, Teil, Projektbezug,
    //    Auftragsnummer, nachtragZu wie vorher). Nur solange das Projekt-
    //    Speichern nicht versucht wurde — und nur auf den EIGENEN letzten
    //    Stand (lastUpdatedAt): hat seitdem jemand gespeichert oder einen Teil
    //    erfasst, überschriebe eine Rücknahme nach id dessen details und
    //    leerte ALLE Teile. Dann bleibt die Annahme stehen, der eigene Teil
    //    gilt als ausstehend, und „Erneut eintragen" holt ihn idempotent nach
    //    (Projektbezug und nachtragZu stehen seit dem Claim am Angebot).
    const revertClaim = async (err: unknown): Promise<unknown> => {
        let state: QuoteAcceptFailureState = { updatedAt: lastUpdatedAt, quote: lastRow, accepted: true, status: targetStatus };
        let reverted = false;
        try {
            const { nachtragZu: _ours, ...rest } = lastDetails || quote.details || {};
            const original = quote.details?.nachtragZu;
            const { data: revertedRows, error: revErr } = await supabase
                .from('quotes')
                .update({
                    status: quote.status,
                    accepted_at: null,
                    order_tranches: [],
                    project_id: quote.projectId ?? null,
                    order_number: quote.orderNumber ?? null,
                    details: original ? { ...rest, nachtragZu: original } : rest,
                })
                .eq('id', quote.id)
                .eq('updated_at', lastUpdatedAt)
                .select('*, customers(name)');
            if (!revErr && revertedRows?.length) {
                const row = mapQuoteRow(revertedRows[0]);
                state = { updatedAt: row.updatedAt, quote: row, accepted: false };
                reverted = true;
            }
        } catch {
            /* Ruecknahme best effort — unten wird der aktuelle Stand gelesen. */
        }
        if (!reverted) {
            // Nicht zurückgenommen (Anker verfehlt oder Fehler): den AKTUELLEN
            // Stand übernehmen — mit Teil, Projektbezug und Auftragsnummer —,
            // sonst zeigte der Editor ein „angenommen" ohne Teil (Altbestand
            // = voll eingetragen) und „Erneut eintragen" erst nach Neuladen.
            try {
                const { data: current } = await supabase
                    .from('quotes').select('*, customers(name)').eq('id', quote.id).maybeSingle();
                if (current) {
                    const row = mapQuoteRow(current);
                    const ordered = row.status === 'accepted' || row.status === 'partial';
                    state = { updatedAt: row.updatedAt, quote: row, accepted: ordered, ...(ordered ? { status: row.status } : {}) };
                }
            } catch {
                /* Lesen best effort — dann bleibt der Zustand „angenommen" vom Claim. */
            }
        }
        return attachAcceptState(err, state);
    };

    let orderNumber = '';
    try {
        // e) Auftragsnummer — gleiche RPC wie acceptQuote.
        orderNumber = await nextDocumentNumber('AB');

        // f) Wie acceptQuote c5: den VOLLSTÄNDIGEN angenommenen Stand schreiben
        //    (Endsummen neu gerechnet, Nachfass-Verlauf vom Server), dazu
        //    Auftragsnummer, Projektbezug und nachtragZu — verankert auf dem Claim.
        const sections = quote.details?.sections ?? DEFAULT_QUOTE_SECTIONS;
        const acceptedItems = computePosNumbers(sections, quote.items || []);
        const totals = calculateQuoteTotals(acceptedItems, quote.discountPercent || 0, quote.taxRate || 0);
        const followUpLog = unionFollowUpLog(lastDetails?.followUpLog, quote.details?.followUpLog);
        const acceptedDetails: QuoteDetails = {
            ...(quote.details || {}),
            sections,
            ...(followUpLog.length > 0 ? { followUpLog } : {}),
            nachtragZu: ref,
        };
        const { data: accRows, error: updErr } = await supabase
            .from('quotes')
            .update({
                ...toQuoteRow({
                    title: quote.title ?? '',
                    quoteDate: quote.quoteDate,
                    validUntil: quote.validUntil,
                    introText: quote.introText,
                    outroText: quote.outroText,
                    paymentTerms: quote.paymentTerms,
                    discountPercent: quote.discountPercent || 0,
                    taxRate: quote.taxRate || 0,
                    items: acceptedItems,
                    details: acceptedDetails,
                    netTotal: totals.netTotal,
                    grossTotal: totals.grossTotal,
                }),
                order_number: orderNumber,
                project_id: ctx.projectId,
                updated_at: new Date().toISOString(),
            })
            .eq('id', quote.id)
            .eq('updated_at', claimedUpdatedAt)
            .select('*, customers(name)');
        if (updErr) throw updErr;
        if (accRows?.length) {
            lastRow = mapQuoteRow(accRows[0]);
        } else {
            // Dazwischen hat jemand gespeichert: dessen Stand bleibt, nur der
            // Bezug wird nachgetragen — frisch gelesen, verankert, drei Versuche.
            console.warn(`[quoteSupplementService] Angebot ${quote.quoteNumber} wurde waehrend der Annahme gespeichert; `
                + 'nur Auftragsnummer/Projektbezug nachgetragen.');
            let linked: Record<string, unknown> | null = null;
            for (let attempt = 0; attempt < 3 && !linked; attempt += 1) {
                const { data: cur, error: curErr } = await supabase
                    .from('quotes').select('*').eq('id', quote.id).maybeSingle();
                if (curErr) throw curErr;
                if (!cur) throw new QuoteNotSavedError('Angebotsbezug konnte nicht gespeichert werden (Angebot nicht gefunden).');
                const { data: linkRows, error: linkErr } = await supabase
                    .from('quotes')
                    .update({
                        order_number: orderNumber,
                        project_id: ctx.projectId,
                        details: { ...((cur.details as QuoteDetails | null) || {}), nachtragZu: ref },
                        updated_at: new Date().toISOString(),
                    })
                    .eq('id', quote.id)
                    .eq('updated_at', cur.updated_at)
                    .select('*, customers(name)');
                if (linkErr) throw linkErr;
                if (linkRows?.length) linked = linkRows[0];
            }
            if (!linked) throw new QuoteNotSavedError('Angebotsbezug konnte nicht gespeichert werden (Angebot wird parallel geändert).');
            lastRow = mapQuoteRow(linked);
        }
        lastUpdatedAt = lastRow.updatedAt;
        lastDetails = lastRow.details;
    } catch (err) {
        throw await revertClaim(err);
    }

    // g) Projekt-LV — derselbe Weg wie „Erneut eintragen" (Notiz-id = Teil-id,
    //    gespeicherte Zuordnung am Teil, appliedAt, Auftragswert best effort,
    //    Projektende). Bis saveProjectToCloud versucht wird, nehmen wir die
    //    Annahme zurück; danach nie mehr.
    let saveAttempted = false;
    const linkedQuote: Quote = lastRow;
    try {
        const applied = await applyTrancheToProject(linkedQuote, tranche.id, {
            actorName: values.actor?.name,
            flatDecisions: values.flatDecisions,
            endDate: values.endDate,
            orderValueCents: values.orderValueCents,
            ...(values.orderValueBaseCents !== undefined ? { orderValueBaseCents: values.orderValueBaseCents } : {}),
            onProjectSaveAttempt: () => { saveAttempted = true; },
        });
        return { quote: applied, orderNumber, projectId: ctx.projectId, applied: true };
    } catch (e) {
        if (!saveAttempted) throw await revertClaim(e);
        console.error('[quoteSupplementService] Nachtrag angenommen, Projekt-LV nicht (vollständig) angepasst', e);
        return { quote: linkedQuote, orderNumber, projectId: ctx.projectId, applied: false, applyError: describeError(e) };
    }
};

export interface CreateSupplementQuoteInput {
    projectId: string;
    /** Angebot, aus dem gestartet wurde (Hauptangebot) — Kunde, Typziffer, Texte. */
    mainQuote?: Quote;
    /** Kunde; fehlt = projects.customer_id bzw. Kunde des Hauptangebots. */
    customerId?: string;
    /** Vorgaben aus quoteConfig (Aufrufer). */
    validUntil?: string;
    taxRate?: number;
    paymentTerms?: string;
}

/**
 * Leeres Nachtragsangebot (Entwurf) zum Projekt anlegen — EIN Insert über
 * createQuote: Titel „Nachtrag <seq> – <Projektname>", details.nachtragZu
 * (Projekt, Hauptangebot, seq), offerType „Nachtragsangebot Nr. <seq>",
 * Texte/Steuer aus dem Hauptangebot, keine Positionen.
 * Wirft mit Klartext, wenn das Projekt keinem Kunden zugeordnet ist oder
 * gesperrt ist (Archiv, Tochter).
 *
 * Die seq ist hier ein Vorschlag: zwei gleichzeitig angelegte Nachträge
 * können dieselbe bekommen — die Annahme prüft sie erneut
 * (loadSupplementContext) und nimmt bei Kollision die nächste freie.
 */
export const createSupplementQuote = async (input: CreateSupplementQuoteInput): Promise<Quote> => {
    const loaded = await loadContext(input.projectId);
    const { ctx } = loaded;
    if (ctx.blocker) throw new Error(ctx.blocker);
    const main = input.mainQuote ?? loaded.mainQuote;
    const customerId = input.customerId || ctx.customerId || main?.customerId;
    if (!customerId) {
        throw new Error('Projekt ist keinem Kunden zugeordnet — bitte zuerst im Projekt den Kunden verknüpfen.');
    }
    const typeDigit = typeDigitFromDocumentNumber(main?.quoteNumber)
        ?? typzifferFuerProjektTyp(loaded.project.data?.metadata?.projectType ?? 'sigeko');

    // Bezug auf das HAUPTangebot: das übergebene, solange es selbst kein
    // Nachtrag ist (Start aus einem angenommenen Nachtrag) — sonst das des
    // Projekts, ersatzweise der Bezug, den jener Nachtrag schon trägt.
    const given = input.mainQuote;
    const givenRef = given?.details?.nachtragZu;
    let mainRef: Parameters<typeof supplementRef>[2];
    if (given && !isSupplementQuote(given)) {
        mainRef = {
            id: given.id,
            quoteNumber: given.quoteNumber,
            orderNumber: given.orderNumber,
            orderDate: given.id === loaded.mainQuote?.id
                ? ctx.mainQuote?.acceptedAt
                : (quoteOrderSummary(given).firstOrderDate || given.acceptedAt),
        };
    } else if (!ctx.mainQuote && givenRef?.projectId === ctx.projectId && givenRef.mainQuoteId && givenRef.mainQuoteNumber) {
        mainRef = { id: givenRef.mainQuoteId, quoteNumber: givenRef.mainQuoteNumber, orderNumber: givenRef.mainOrderNumber, orderDate: givenRef.mainOrderDate };
    }
    const ref = supplementRef(ctx, undefined, mainRef);
    const md = main?.details;
    const details: QuoteDetails = {
        sections: [],
        offerType: `Nachtragsangebot Nr. ${ctx.seq}`,
        nachtragZu: ref,
        ...(md?.siteStreet ? { siteStreet: md.siteStreet } : {}),
        ...(md?.siteZip ? { siteZip: md.siteZip } : {}),
        ...(md?.siteCity ? { siteCity: md.siteCity } : {}),
        ...(md?.agbText ? { agbText: md.agbText } : {}),
        ...(md?.privacyText ? { privacyText: md.privacyText } : {}),
    };
    const name = ctx.projectName || ctx.projectNumber || 'Projekt';
    const taxRate = main?.taxRate ?? input.taxRate;
    const paymentTerms = main?.paymentTerms || input.paymentTerms;

    return createQuote({
        customerId,
        title: `Nachtrag ${ctx.seq} – ${name}`,
        items: [],
        discountPercent: 0,
        ...(taxRate !== undefined ? { taxRate } : {}),
        ...(input.validUntil ? { validUntil: input.validUntil } : {}),
        ...(main?.introText ? { introText: main.introText } : {}),
        ...(main?.outroText ? { outroText: main.outroText } : {}),
        ...(paymentTerms ? { paymentTerms } : {}),
        details,
        typeDigit,
    });
};
